/**
 * In-app message inbox — persisted to SQLite so unread state, dismissals,
 * and achievement history survive reloads. Separate from OS push
 * notifications (handled in notifications.ts).
 *
 * Architecture: writes go to the `message` SQLite table; an in-memory
 * cache mirrors what's persisted so React subscribers get synchronous
 * reads. After every mutation we re-fetch from the DB and emit to
 * subscribers — the table is small (typically <100 rows) so this is
 * cheap, and it keeps the cache and DB in lockstep without manual
 * invariant tracking.
 *
 * Seed messages use the `seed_key` UNIQUE column with INSERT OR IGNORE,
 * so a seeded message is never re-created if the user deleted it.
 */

import type { SQLiteDatabase } from 'expo-sqlite';
import { useSQLiteContext } from 'expo-sqlite';
import { useEffect, useState } from 'react';

import type { MessageRow } from '@/db/types';

export type MessageTone = 'info' | 'achievement' | 'warning' | 'success';

export interface AppMessage {
  id: string;
  title: string;
  body?: string;
  ts: number;
  read: boolean;
  tone: MessageTone;
  /**
   * Suppress this message from the top-of-page ticker bar while still
   * showing it in the inbox. Use for "we shipped a new feature" or other
   * non-urgent announcements — the ticker is reserved for things the user
   * needs to act on (onboarding nudges, missing system info, expiring
   * warranties, weather alerts). Defaults to false (visible in ticker).
   */
  tickerHidden?: boolean;
  /** Stable identifier for seeded messages, used by INSERT OR IGNORE so
   *  seeds are idempotent and survive deletions. NULL for runtime
   *  posts. */
  seedKey?: string;
  /** Optional deep-link route. When set, the message is rendered as a
   *  tappable card that navigates here on tap (instead of the default
   *  "open the inbox detail" behavior). */
  ctaRoute?: string;
  /** Property id this message belongs to (schema v39). Inbox renders a
   *  "from [home nickname]" chip when set. NULL/undefined = user-wide. */
  propertyId?: string;
}

function rowToMessage(r: MessageRow): AppMessage {
  return {
    id: r.id,
    title: r.title,
    body: r.body ?? undefined,
    ts: r.ts,
    read: r.read === 1,
    tone: r.tone,
    tickerHidden: r.ticker_hidden === 1,
    seedKey: r.seed_key ?? undefined,
    ctaRoute: r.cta_route ?? undefined,
    propertyId: r.property_id ?? undefined,
  };
}

// In-memory cache mirroring the persisted state. Listeners notify
// React components when the cache changes.
let cache: AppMessage[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

async function reloadCache(db: SQLiteDatabase): Promise<void> {
  const rows = await db.getAllAsync<MessageRow>(
    `SELECT * FROM message WHERE deleted_at IS NULL ORDER BY ts DESC`
  );
  cache = rows.map(rowToMessage);
  emit();
}

/**
 * One-shot hook that loads the persisted inbox into the cache on app
 * boot, then runs the seed pass. Mount inside SQLiteProvider — the
 * hook reaches for the DB via `useSQLiteContext`.
 *
 * Re-runs are cheap: the seed INSERT OR IGNORE skips already-seeded
 * rows, and the cache reload is one indexed query.
 */
export function useMessagesInit(): void {
  const db = useSQLiteContext();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await reloadCache(db);
        if (cancelled) return;
        await seedMessagesIfNeeded(db);
      } catch (err) {
        console.warn('useMessagesInit failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [db]);
}

export interface PostMessageInput {
  tone: MessageTone;
  title: string;
  body?: string;
  tickerHidden?: boolean;
  /** Seed-only — see AppMessage.seedKey. Runtime posts leave this null. */
  seedKey?: string;
  /** Optional deep-link route. When set, tapping the message routes
   *  there instead of the inbox detail view. Used by seed messages
   *  like the welcome card to send users straight into /profile-setup. */
  ctaRoute?: string;
  /** Optional property id. When set, the message is tagged as belonging
   *  to a specific home — the inbox shows a "from [nickname]" chip and
   *  taps that include the message can auto-switch to that home. NULL
   *  means user-wide (default for app-level messages). */
  propertyId?: string | null;
}

function genId(): string {
  return `msg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Post a new message. Idempotent if `seedKey` is set (INSERT OR IGNORE
 * on the UNIQUE seed_key index — won't resurrect a previously-deleted
 * seed). Cache is refreshed and subscribers notified on success.
 */
export async function postMessage(
  db: SQLiteDatabase,
  msg: PostMessageInput
): Promise<void> {
  const id = genId();
  const ts = Date.now();
  const tickerHidden = msg.tickerHidden ? 1 : 0;

  if (msg.seedKey) {
    await db.runAsync(
      `INSERT OR IGNORE INTO message
       (id, title, body, ts, read, tone, ticker_hidden, seed_key, cta_route, property_id, created_at, updated_at)
       VALUES (?, ?, ?, ?, 0, ?, ?, ?, ?, ?, ?, ?)`,
      id,
      msg.title,
      msg.body ?? null,
      ts,
      msg.tone,
      tickerHidden,
      msg.seedKey,
      msg.ctaRoute ?? null,
      msg.propertyId ?? null,
      ts,
      ts
    );
  } else {
    await db.runAsync(
      `INSERT INTO message
       (id, title, body, ts, read, tone, ticker_hidden, seed_key, cta_route, property_id, created_at, updated_at)
       VALUES (?, ?, ?, ?, 0, ?, ?, NULL, ?, ?, ?, ?)`,
      id,
      msg.title,
      msg.body ?? null,
      ts,
      msg.tone,
      tickerHidden,
      msg.ctaRoute ?? null,
      msg.propertyId ?? null,
      ts,
      ts
    );
  }

  await reloadCache(db);
}

export async function markRead(db: SQLiteDatabase, id: string): Promise<void> {
  const t = Date.now();
  await db.runAsync(
    `UPDATE message SET read = 1, updated_at = ? WHERE id = ?`,
    t,
    id
  );
  await reloadCache(db);
}

export async function markUnread(db: SQLiteDatabase, id: string): Promise<void> {
  const t = Date.now();
  await db.runAsync(
    `UPDATE message SET read = 0, updated_at = ? WHERE id = ?`,
    t,
    id
  );
  await reloadCache(db);
}

export async function markAllRead(db: SQLiteDatabase): Promise<void> {
  const t = Date.now();
  await db.runAsync(
    `UPDATE message SET read = 1, updated_at = ?
       WHERE read = 0 AND deleted_at IS NULL`,
    t
  );
  await reloadCache(db);
}

/**
 * Bulk read-state toggle. `read=true` marks all selected as read,
 * `read=false` flips them to unread. Used by the inbox's bulk action
 * bar; nothing else should need it.
 */
export async function markReadBulk(
  db: SQLiteDatabase,
  ids: readonly string[],
  read: boolean
): Promise<void> {
  if (ids.length === 0) return;
  const t = Date.now();
  const placeholders = ids.map(() => '?').join(',');
  await db.runAsync(
    `UPDATE message SET read = ?, updated_at = ?
       WHERE id IN (${placeholders})`,
    read ? 1 : 0,
    t,
    ...ids
  );
  await reloadCache(db);
}

export async function dismissMessage(
  db: SQLiteDatabase,
  id: string
): Promise<void> {
  const t = Date.now();
  await db.runAsync(
    `UPDATE message SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    id
  );
  await reloadCache(db);
}

export async function dismissMessages(
  db: SQLiteDatabase,
  ids: readonly string[]
): Promise<void> {
  if (ids.length === 0) return;
  const t = Date.now();
  const placeholders = ids.map(() => '?').join(',');
  await db.runAsync(
    `UPDATE message SET deleted_at = ?, updated_at = ?
       WHERE id IN (${placeholders})`,
    t,
    t,
    ...ids
  );
  await reloadCache(db);
}

export function useMessages(): AppMessage[] {
  const [snap, setSnap] = useState<AppMessage[]>(cache);
  useEffect(() => {
    setSnap([...cache]);
    const fn = () => setSnap([...cache]);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return snap;
}

export function useUnreadCount(): number {
  const msgs = useMessages();
  return msgs.filter((m) => !m.read).length;
}

/**
 * Subset of messages eligible for the top-of-page ticker.
 *
 * Two filters layered:
 *   1. `tickerHidden` — feature-announcement / weekly-prompt content
 *      lives in the inbox only. The ticker is reserved for important
 *      home/account-level messages.
 *   2. `read` — once the user has read (or actioned) a message, it
 *      stops appearing in the ticker. The bar should only nag about
 *      things the user HASN'T seen yet. The inbox still shows the
 *      message in its full history, just not at the top of every page.
 *
 * If both filters leave nothing, the AppHeader's render branch hides
 * the bar entirely (no empty 32-px strip lingering).
 */
export function useTickerMessages(): AppMessage[] {
  const msgs = useMessages();
  return msgs.filter((m) => !m.tickerHidden && !m.read);
}

/**
 * Insert any first-run seed messages. Safe to call repeatedly — each
 * seed has a `seed_key` UNIQUE in the DB and uses INSERT OR IGNORE.
 * Notably this means a user-deleted seed STAYS deleted; the row's
 * deleted_at sticks and the UNIQUE constraint prevents re-insertion.
 */
export async function seedMessagesIfNeeded(db: SQLiteDatabase): Promise<void> {
  // Welcome card — top-of-page ticker eligible. The ticker is reserved
  // for "important" account-level messages (this one explains the app
  // itself). Tap takes the user to the tune walkthrough, which is the
  // single highest-leverage next step right after onboarding.
  await postMessage(db, {
    tone: 'info',
    title: 'Welcome to Homestead',
    body: "Your home plan is ready. Tap to sharpen it with a few quick chip picks — then explore the tabs to see what's due.",
    seedKey: 'welcome-v4',
    ctaRoute: '/profile-setup',
  });
  // How-it-works — inbox-only. Stays out of the ticker so it doesn't
  // crowd the home tab; surfaces when the user opens the bell, which
  // is exactly when "how does this work?" is the question they're
  // asking. Three sentences max — anything longer doesn't get read.
  await postMessage(db, {
    tone: 'info',
    title: 'How Homestead works',
    body: 'Each task on your schedule shows when it\'s due and why it matters — tap "How & why" to learn before you do. Mark done to keep your streak. The + button anywhere logs a task, expense, or system.',
    tickerHidden: true,
    seedKey: 'how-it-works-v1',
  });
  // Wave 65 (user call): feature-announcement seeds retired from the
  // inbox — notifications are for the user's HOME, not our changelog.
  // The sweep below also soft-deletes the announcement on devices that
  // already have it (idempotent; a NULL-deleted_at guard keeps it from
  // rewriting the row on every boot).
  await db.runAsync(
    `UPDATE message SET deleted_at = ?, updated_at = ?
     WHERE seed_key = 'feature-desktop-sidebar' AND deleted_at IS NULL`,
    Date.now(),
    Date.now()
  ).catch(() => {});
}

/**
 * Back-compat shim — old call sites called this with no args. Maintained
 * so we don't have to chase down every caller; new code should use
 * `useMessagesInit()` or call `seedMessagesIfNeeded(db)` directly.
 */
export function seedMessagesIfEmpty(): void {
  // No-op: seeding is now handled by useMessagesInit() inside the
  // SQLiteProvider tree. This stub keeps existing imports compiling.
}
